import { ProcessAutomationIcon, SpiderIcon, WhmcsIcon } from '@patternfly/react-icons';
import React from 'react';
import {
  Bullseye,
  Button,
  ButtonVariant,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  PageSection,
  PageSectionVariants,
  Stack,
  StackItem,
  Content,
  Title,
  Grid,
  GridItem,
  ContentVariants,
} from '@patternfly/react-core';
import { useAuth } from 'react-oidc-context';
import { returnToSigninArgs } from '../utils/returnTo';
import { AncalagonLogo } from '../assets/icons/Ancalagon-Logo';
import ServerIcon from '@patternfly/react-icons/dist/esm/icons/server-icon';
import NetworkIcon from '@patternfly/react-icons/dist/esm/icons/network-wired-icon';
import DatabaseIcon from '@patternfly/react-icons/dist/esm/icons/database-icon';
import CubeIcon from '@patternfly/react-icons/dist/esm/icons/cube-icon';
import SyncAltIcon from '@patternfly/react-icons/dist/esm/icons/sync-alt-icon';
import ChartLineIcon from '@patternfly/react-icons/dist/esm/icons/chart-line-icon';
import CloudIcon from '@patternfly/react-icons/dist/esm/icons/cloud-icon';

function Unauthenticated() {
  const auth = useAuth();

  const authority = 'https://auth.ancalagon.be/realms/Manager';
  const clientId = 'managerfrontend';
  const redirectUri = (process.env.REACT_APP_URL as string) || window.location.origin;

  const signupUrl = `${authority}/protocol/openid-connect/registrations?client_id=${encodeURIComponent(
    clientId,
  )}&response_type=code&scope=${encodeURIComponent('openid profile email')}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}`;

  return (
    <>
      <PageSection variant={PageSectionVariants.default}>
        <Bullseye>
          <Stack hasGutter style={{ maxWidth: '800px', textAlign: 'center' }}>
            <StackItem>
              <Bullseye>
                <AncalagonLogo height={80} />
              </Bullseye>
            </StackItem>
            <StackItem>
              <Content>
                <Title headingLevel="h1" size="2xl">
                  <Bullseye>Ancalagon Manager</Bullseye>
                </Title>
                <Content component={ContentVariants.h2}>
                  <Bullseye>IT Infrastructure Management &amp; Cloud Services Platform</Bullseye>
                </Content>
              </Content>
            </StackItem>
            <StackItem>
              <Content>
                Ancalagon Manager is a comprehensive web application designed for managing your IT infrastructures and
                cloud services that we provides. This platform enables you to efficiently manage, monitor, and control
                your datacenter resources including virtual machines, containers, networks, and backup systems.
              </Content>
            </StackItem>
          </Stack>
        </Bullseye>
      </PageSection>

      <PageSection variant={PageSectionVariants.default}>
        <Stack hasGutter>
          <StackItem>
            <Grid hasGutter md={6} style={{ marginLeft: '1rem', marginRight: '1rem' }}>
              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <ServerIcon /> Virtual Machine Management
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Deploy, monitor, and manage virtual machines. Control VM lifecycle operations including start, stop,
                    restart, and resource allocation.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <CubeIcon /> Container Services
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Manage containerized applications and services. Deploy, scale, and monitor container workloads
                    across your infrastructure.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <NetworkIcon /> Network Configuration
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Configure and manage network infrastructure. Control network settings, routing, and connectivity for
                    your resources.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <DatabaseIcon /> Backup Management
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Configure automated backups and manage data protection. Schedule, monitor, and restore backups for
                    your infrastructure.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <SyncAltIcon /> Service Operations
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Perform operational tasks on your services and infrastructure. Restart services, update
                    configurations, and manage resource states.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <ChartLineIcon /> Billing &amp; Monitoring
                    </Title>
                  </CardHeader>
                  <CardBody>
                    Track resource usage, monitor performance metrics, and manage billing. Access invoices and usage
                    reports for your infrastructure.
                  </CardBody>
                </Card>
              </GridItem>

              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <CloudIcon /> Managed Applications
                    </Title>
                  </CardHeader>
                  <CardBody>
                    We manage and host applications for our customers, including GitLab Hosted, Vaultwarden, Netbox, and
                    other critical services tailored to your infrastructure needs.
                  </CardBody>
                </Card>
              </GridItem>
              <GridItem>
                <Card isCompact>
                  <CardHeader>
                    <Title headingLevel="h4" size="md">
                      <ProcessAutomationIcon /> CI/CD, DevOps and GitOps
                    </Title>
                  </CardHeader>
                  <CardBody>
                    We develop and manage your pipelines and infrastructure using GitOps principles to ensure
                    consistency and reliability in your deployments.
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          </StackItem>

          <StackItem>
            <Divider />
          </StackItem>

          <StackItem>
            <Bullseye>
              <Card style={{ maxWidth: '600px' }}>
                <CardHeader>
                  <Title headingLevel="h3" size="lg">
                    Get Started
                  </Title>
                </CardHeader>
                <CardBody>
                  <Stack hasGutter>
                    <StackItem>
                      <Content>
                        Sign in to access your infrastructure management dashboard and start managing your servers,
                        networks, containers, backups, and cloud services.
                      </Content>
                    </StackItem>
                    <StackItem>
                      <Stack hasGutter>
                        <StackItem>
                          <Button
                            variant={ButtonVariant.primary}
                            onClick={() => void auth.signinRedirect(returnToSigninArgs())}
                            isBlock
                          >
                            Sign in
                          </Button>
                        </StackItem>
                        <StackItem>
                          <Button
                            variant={ButtonVariant.secondary}
                            onClick={() =>
                              void auth.signinRedirect({
                                ...returnToSigninArgs(),
                                extraQueryParams: { kc_action: 'REGISTER' },
                              })
                            }
                            isBlock
                          >
                            Create an account
                          </Button>
                        </StackItem>
                      </Stack>
                    </StackItem>
                  </Stack>
                </CardBody>
                <Divider />
                <CardFooter>
                  <Stack hasGutter>
                    <StackItem>
                      <Content>
                        <small>
                          By continuing, you agree to our{' '}
                          <Button
                            component="a"
                            variant="link"
                            isInline
                            href="https://manager.ancalagon.be/tos.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms of Service
                          </Button>{' '}
                          and{' '}
                          <Button
                            component="a"
                            variant="link"
                            isInline
                            href="https://manager.ancalagon.be/privacy.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Privacy Policy
                          </Button>
                          .
                        </small>
                      </Content>
                    </StackItem>
                  </Stack>
                </CardFooter>
              </Card>
            </Bullseye>
          </StackItem>
        </Stack>
      </PageSection>
    </>
  );
}

export default Unauthenticated;
